import { CaretRightOutlined, DeleteFilled, RocketFilled } from '@ant-design/icons'
import { Badge } from 'antd'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { GroupHoverCard, PostTypeIcon, Timestamp, UserAvatar, UserHoverCard } from '@/components'
import { useIsFeatureToggleOn } from '@/components/feature-toggle/feature-toggle.hooks'
import { PostType, RequestedPostFragment } from '@/generated/graphql'
import { routes } from '@/routes'

import { PostCardMenu } from './post-card-menu.component'
import { usePostImpactUpdate } from './post-card.hooks'
import s from './post-card.module.less'

interface PostCardHeaderProps {
  post: RequestedPostFragment
  isGroup: boolean
  isUser: boolean
  isSection: boolean
  isTop: boolean
  hideMenu?: boolean
  hideAvatar?: boolean
  showGroupInfo: boolean
  isRecommended: boolean
}

export const PostCardHeader: React.FC<PostCardHeaderProps> = ({
  post,
  isGroup,
  isUser,
  isSection,
  isTop,
  hideMenu,
  hideAvatar = false,
  isRecommended,
  showGroupInfo,
}) => {
  const [t] = useTranslation()
  const isImpactFeatureToggleOn = useIsFeatureToggleOn('impact')
  const impact = usePostImpactUpdate(post.postId)

  const featuredIcon = !!post.featuredAt && <RocketFilled />

  const username = (
    <div className={s.userName}>
      {!post.creator ? (
        'Agoran'
      ) : (
        <UserHoverCard email={post.creator.email}>
          <Link onClick={(e) => e.stopPropagation()} to={routes.users.page(post.creator.email)}>
            {post.creator.name}
          </Link>
        </UserHoverCard>
      )}
    </div>
  )

  const groupRightText = showGroupInfo && post.group && (
    <div>
      <GroupHoverCard groupId={post.group.groupId}>
        <Link onClick={(e) => e.stopPropagation()} to={routes.groups.page(post.group.groupId)}>
          {post.group.name}
        </Link>
      </GroupHoverCard>
    </div>
  )

  const userRightText = post.receiver && (
    <div>
      <UserHoverCard email={post.receiver.email}>
        <Link onClick={(e) => e.stopPropagation()} to={routes.users.page(post.receiver.email)}>
          {post.receiver.name}
        </Link>
      </UserHoverCard>
    </div>
  )

  let icon
  let rightText = groupRightText
  switch (post.postType) {
    case PostType.Thanks:
      icon = <PostTypeIcon type={post.postType} />
      rightText = userRightText
      break
    case PostType.Forum:
    case PostType.Journal:
    case PostType.Event:
    case PostType.Honor:
      icon = <PostTypeIcon type={post.postType} />
      break
    case PostType.Introduction:
    case PostType.Announcement:
    case PostType.General:
    default:
      icon = <CaretRightOutlined />
      break
  }

  const showIcon = (showGroupInfo && post.group) || post.receiver
  const headerIcon = showIcon && <div className={s.headerIcon}>{icon}</div>
  const sharedText = post.originalPostId && <div className={s.sharedText}>{t('post.sharedPost')}</div>
  const archivedIcon = !!post.archivedAt && <DeleteFilled />
  const impactBadge = isImpactFeatureToggleOn && (
    <div className={s.impactBadge}>
      <Badge
        count={impact}
        overflowCount={10000000}
        offset={[-4, -4]}
        style={{ backgroundColor: 'white', color: '#48bbb5' }}
      />
    </div>
  )

  return (
    <div className={s.headerContainer}>
      {!hideAvatar &&
        (!post.creator ? (
          <UserAvatar className={s.avatar} size='large' user={null} />
        ) : (
          <UserHoverCard email={post.creator.email}>
            <Link onClick={(e) => e.stopPropagation()} to={routes.users.page(post.creator.email)}>
              <UserAvatar className={s.avatar} size='large' user={post.creator} />
            </Link>
          </UserHoverCard>
        ))}
      <div className={s.leftWrap}>
        <div className={s.headerWrap}>
          {featuredIcon}
          {username}
          {headerIcon}
          {rightText}
          {sharedText}
          {archivedIcon}
        </div>
        <div className={s.time}>
          <Timestamp time={post.createdAt} />
        </div>
      </div>
      {impactBadge}
      {!hideMenu && (
        <PostCardMenu
          isRecommended={isRecommended}
          post={post}
          isSection={isSection}
          isGroup={isGroup}
          isUser={isUser}
          isTop={isTop}
        />
      )}
    </div>
  )
}
